/*
    This may not be a perfect fit for a snackbar, but that
    feels like the closest UI concept, given the attributes:

    - Snackbars provide updates on an app's processes
        (i.e. are informational)
    - Dismissive snackbars appear temporarily, and disappear on
        their own without requiring user input.  Non-dismissive
        snackbars persist until the user takes an action or
        selects the close affordance.
        (This is a dismissive snackbar.)
    - Snackbars are placed in the most suitable area of the UI

    Reference: https://m3.material.io/components/snackbar/overview
*/

import React, {
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';

import ButtonClose from '../components/general-components/button-close';
import { AVContext } from '../contexts/audio-video';
import { GreenroomState as GS } from '../stores/types/types';
import AudioAnalyzer from '../utils/audio-context';
import useUserActions from './use-user-actions';

interface UnmuteSnackbarProps {
	animationDurationMs?: number;
	audioPollingIntervalMs?: number;
	debugUiMode?: boolean;
	coolDownSeconds?: number;
	showForSeconds?: number;
	volumeThreshold?: number;
}

/*
	Warning: changing `animationDurationMs` will require updating
	`$snackbar-animation-duration` in
	`src/scss/modules/_use-unmute-snackbar.scss`
*/
const useUnmuteSnackbar = ({
	animationDurationMs = 250,
	audioPollingIntervalMs = 100,
	debugUiMode = false,
	coolDownSeconds = 300,
	showForSeconds = 5,
	volumeThreshold = 10,
}: UnmuteSnackbarProps = {}): JSX.Element => {
	// Redux state
	const { webrtcID } = useSelector((state: GS) => state.SessionReducer);
	const { mainVideoStream } = useSelector((state: GS) => state.VideoReducer);

	// Local state
	const [isShowing, setIsShowing] = useState<boolean>(false);
	const [audioMuted, setAudioMuted] = useState<boolean>(false);

	// Refs
	const audioAnalyzer = useRef<AudioAnalyzer | null>(null);
	const audioMuteAlertTimeout = useRef<NodeJS.Timeout | null>(null);
	const coolDownTimer = useRef<NodeJS.Timeout | null>(null);
	const nodeRef = useRef<HTMLDivElement>(null);

	// Context / Hooks
	const { getAV } = useContext(AVContext);
	const { audio } = getAV();

	const { toggleAudio } = useUserActions();

	// Set audioMuted state from AV context when webrtcID changes
	useEffect(() => {
		if (webrtcID) {
			setAudioMuted(!audio);
		}
	}, [webrtcID, audio]);

	const resetAlertShowing = () => {
		setIsShowing(false);

		if (audioMuteAlertTimeout.current) {
			clearTimeout(audioMuteAlertTimeout.current);
			audioMuteAlertTimeout.current = null;
		}
	};

	const resetAlertCoolDown = () => {
		if (coolDownTimer.current) {
			clearTimeout(coolDownTimer.current);
			coolDownTimer.current = null;
		}
	};

	// This will be called at an interval of audioPollingIntervalMs by the
	// audio analyzer when the volume changes.
	const watchAnalyzerChanges = (vol: number) => {
		if (
			vol > volumeThreshold
			&& !isShowing
			&& !coolDownTimer.current
			&& !audioMuteAlertTimeout.current
		) {
			setIsShowing(true);

			// Timer for showing the alert
			audioMuteAlertTimeout.current = setTimeout(() => {
				resetAlertShowing();
			}, showForSeconds * 1000);

			// Timer for the cool down period when we do not want the alert to show
			coolDownTimer.current = setTimeout(() => {
				resetAlertCoolDown();
			}, (coolDownSeconds + showForSeconds) * 1000);
		}
	};

	// Set up the audio analyzer for the current stream when audio is muted
	// Destroy the analyzer and reset timers when audio is unmuted
	useEffect(() => {
		if (audioAnalyzer.current) {
			audioAnalyzer.current.destroy();
			audioAnalyzer.current = null;
		}

		if (mainVideoStream && mainVideoStream.getAudioTracks()[0] && audioMuted) {
			audioAnalyzer.current = new AudioAnalyzer(
				mainVideoStream,
				watchAnalyzerChanges,
				audioPollingIntervalMs,
			);
		} else {
			resetAlertShowing();
			resetAlertCoolDown();
		}
	}, [mainVideoStream, audioMuted]);

	return (
		<CSSTransition
			in={
				(isShowing && webrtcID && mainVideoStream && audioMuted)
				|| debugUiMode
			}
			nodeRef={nodeRef}
			timeout={animationDurationMs}
			classNames="snackbar"
			unmountOnExit
		>
			<div className="unmute-snackbar bl-font-l1" ref={nodeRef}>
				<div className="unmute-snackbar__text">Trying to Talk?</div>
				<button
					className="unmute-snackbar__action"
					onClick={() => {
						if (webrtcID && (audioMuted || debugUiMode)) {
							toggleAudio();
						}
					}}
					data-testid="unmute-snackbar__action"
				>
					Unmute
				</button>
				<ButtonClose
					onClick={resetAlertShowing}
				/>
			</div>
		</CSSTransition>
	);
};

export default useUnmuteSnackbar;
